import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Copy, Check, Link as LinkIcon, MessageCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';

/**
 * Modal para generar y compartir un link de entrevista del alumno (Fase B).
 * Al abrirse, crea el token en el backend y muestra:
 *  - Link copiable
 *  - Botón "Compartir por WhatsApp"
 *  - Vencimiento (7 días)
 */
export function StudentIntakeLinkModal({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [copied, setCopied] = useState(false);

  React.useEffect(() => {
    if (!open) {
      // Limpiar estado al cerrar
      setTokenData(null);
      setCopied(false);
      return;
    }
    // Al abrir, generar token
    (async () => {
      setLoading(true);
      try {
        const data = await api.createStudentIntakeToken();
        setTokenData(data);
      } catch (e) {
        toast.error(e.message || 'No se pudo generar el link');
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, onOpenChange]);

  // El link apunta al mismo sitio desde el que se genera: el link vive en la
  // base de ESE entorno. En producción es el dominio público; en prueba, el
  // sitio de prueba (si apuntara a producción, el alumno vería "link inválido").
  const displayLink = tokenData?.token
    ? `${window.location.origin}/entrevista/${tokenData.token}`
    : '';

  const handleCopy = async () => {
    if (!displayLink) return;
    // Estrategia 1: Clipboard API moderna (falla en iframes con permisos restringidos).
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(displayLink);
        setCopied(true);
        toast.success('Link copiado');
        setTimeout(() => setCopied(false), 2000);
        return;
      }
    } catch { /* fallthrough al fallback */ }
    // Estrategia 2 (fallback): textarea temporal + execCommand('copy'). Funciona
    // en iframes del preview y navegadores viejos.
    try {
      const ta = document.createElement('textarea');
      ta.value = displayLink;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '0';
      ta.style.left = '0';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, displayLink.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) {
        setCopied(true);
        toast.success('Link copiado');
        setTimeout(() => setCopied(false), 2000);
      } else {
        throw new Error('execCommand copy returned false');
      }
    } catch {
      // Último recurso: seleccionar el link visible para que el usuario copie manualmente
      const el = document.querySelector('[data-testid="intake-link-text"]');
      if (el && window.getSelection) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        toast.info('Copialo con Ctrl+C / Cmd+C');
      } else {
        toast.error('No se pudo copiar. Copialo manualmente.');
      }
    }
  };

  const handleWhatsApp = () => {
    if (!displayLink) return;
    const text = `Hola, completá tu entrevista previa acá: ${displayLink}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const expiresText = tokenData?.expires_at
    ? new Date(tokenData.expires_at).toLocaleDateString('es-AR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
      })
    : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="student-intake-modal">
        <DialogHeader>
          <DialogTitle className="font-['Inter'] flex items-center gap-2">
            <LinkIcon className="h-5 w-5 text-[#1F3A5F]" />
            Link de entrevista del alumno
          </DialogTitle>
          <DialogDescription className="font-['Inter']">
            Compartí este link con tu alumno para que complete la pre-carga sin login. Es de un solo uso.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8" data-testid="intake-modal-loading">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : tokenData ? (
          <div className="space-y-4 pt-2">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1 font-['Inter']">Link único</p>
              <p
                className="text-sm text-gray-800 break-all font-mono select-all"
                data-testid="intake-link-text"
              >
                {displayLink}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={handleCopy}
                variant="outline"
                className="flex-1 font-['Inter']"
                data-testid="intake-copy-btn"
              >
                {copied ? (
                  <><Check className="h-4 w-4 mr-2" />Copiado</>
                ) : (
                  <><Copy className="h-4 w-4 mr-2" />Copiar link</>
                )}
              </Button>
              <Button
                onClick={handleWhatsApp}
                className="flex-1 bg-[#25D366] hover:bg-[#1EBE5A] text-white font-['Inter']"
                data-testid="intake-whatsapp-btn"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Compartir por WhatsApp
              </Button>
            </div>
            <p className="text-xs text-gray-500 font-['Inter']" data-testid="intake-expires-text">
              El link vence el <strong>{expiresText}</strong> (7 días) y solo puede usarse una vez.
            </p>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
